import Tooltip from "@/v2-ui/tooltip"
import Icon from "@/v2-ui/icon"
import cn from "@/v2-ui/utils/utils.cn"
import {
  countryFlagEmoji,
  countryName,
} from "@/v2-backoffice-client/signIn/outsideEea/outsideEea.utils"

export type OutsideEeaBadgeProps = {
  // ISO alpha-2 codes of the non-EEA countries the user signed in from, already
  // ordered (the lead country first).
  countries: string[]
  className?: string
}

// OutsideEeaBadge is the shared "signed in from outside the EU/EEA" flag: a
// destructive pill showing the lead non-EEA country by name (+N more), with the
// full list on hover. Reused by the sign-ins Users table, the user-panel info
// grid, and the user-panel sign-in tab. Renders nothing when there are none.
function OutsideEeaBadge(props: OutsideEeaBadgeProps) {
  const { countries, className } = props
  if (countries.length === 0) return null

  const items = countries.map((code) => ({
    code,
    name: countryName(code),
    flag: countryFlagEmoji(code),
  }))
  const lead = items[0]
  const extra = items.length - 1

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <span
          className={cn(
            "inline-flex items-center gap-1 font-medium text-destructive-foreground",
            className,
          )}
        >
          {lead.flag ? (
            <span aria-hidden className="text-sm leading-none">
              {lead.flag}
            </span>
          ) : (
            <Icon src="flag" size={12} />
          )}
          {lead.name}
          {extra > 0 && (
            <span className="text-muted-foreground font-normal">+{extra}</span>
          )}
        </span>
      </Tooltip.Trigger>

      <Tooltip.Content side="top" className="ui-tooltip-content--panel">
        <div className="flex flex-col gap-1 p-3">
          <p className="font-medium">Signed in from outside the EU/EEA</p>
          <ul className="text-muted-foreground pl-2">
            {items.map((item) => (
              <li key={item.code} className="flex items-center gap-1.5">
                {item.flag ? (
                  <span aria-hidden className="leading-none">
                    {item.flag}
                  </span>
                ) : (
                  <Icon src="flag" size={12} />
                )}
                {item.name}
              </li>
            ))}
          </ul>
        </div>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default OutsideEeaBadge
